import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft,
  Search,
  Download,
  Save,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Package,
  Filter,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Check,
  Layers,
} from "lucide-react";

const STATUS_CONFIG = {
  complete: { label: "Complete", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  missing_weight: { label: "Missing Weight", color: "bg-amber-100 text-amber-800", icon: AlertTriangle },
  missing_dimensions: { label: "Missing Dims", color: "bg-orange-100 text-orange-800", icon: AlertTriangle },
  missing_all: { label: "Missing All", color: "bg-red-100 text-red-800", icon: XCircle },
};

const BulkShippingEditorPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");

  // Edited rows tracking: { product_id: { weight, shipping_length, shipping_width, shipping_height } }
  const [editedRows, setEditedRows] = useState({});

  // Selection for mass apply
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMassApply, setShowMassApply] = useState(false);
  const [massApplyData, setMassApplyData] = useState({ weight: "", shipping_length: "", shipping_width: "", shipping_height: "" });

  // Categories list
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/categories`, { headers }).then(r => {
      setCategories(Array.isArray(r.data) ? r.data : r.data?.categories || []);
    }).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (activeFilter) params.filter = activeFilter;
      if (searchQuery) params.search = searchQuery;
      if (categoryFilter) params.category = categoryFilter;
      if (brandFilter) params.brand = brandFilter;

      const resp = await axios.get(`${API}/admin/shipping-specs`, { headers, params });
      setProducts(resp.data.products || []);
      setTotal(resp.data.total || 0);
      setTotalPages(resp.data.pages || 1);
      setSummary(resp.data.summary || {});
    } catch (err) {
      toast.error("Failed to load shipping specs");
    } finally {
      setLoading(false);
    }
  }, [page, activeFilter, searchQuery, categoryFilter, brandFilter, token]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFieldChange = (productId, field, value) => {
    setEditedRows(prev => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value },
    }));
  };

  const getFieldValue = (product, field) => {
    if (editedRows[product.product_id] && editedRows[product.product_id][field] !== undefined) {
      return editedRows[product.product_id][field];
    }
    return product[field] !== null && product[field] !== undefined ? String(product[field]) : "";
  };

  const isRowEdited = (productId) => !!editedRows[productId];

  const saveRow = async (productId) => {
    const edits = editedRows[productId];
    if (!edits) return;

    const payload = { product_id: productId };
    for (const [field, val] of Object.entries(edits)) {
      const num = val === "" ? null : parseFloat(val);
      if (num !== null && (isNaN(num) || num < 0)) {
        toast.error(`Invalid value for ${field.replace("shipping_", "")}`);
        return;
      }
      payload[field] = num;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/shipping-specs/bulk`, [payload], { headers });
      toast.success("Saved");
      setEditedRows(prev => { const n = { ...prev }; delete n[productId]; return n; });
      fetchProducts();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveAllEdited = async () => {
    const updates = [];
    for (const [productId, edits] of Object.entries(editedRows)) {
      const payload = { product_id: productId };
      let valid = true;
      for (const [field, val] of Object.entries(edits)) {
        const num = val === "" ? null : parseFloat(val);
        if (num !== null && (isNaN(num) || num < 0)) {
          toast.error(`Invalid value in row for ${field}`);
          valid = false;
          break;
        }
        payload[field] = num;
      }
      if (valid) updates.push(payload);
    }

    if (updates.length === 0) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const resp = await axios.put(`${API}/admin/shipping-specs/bulk`, updates, { headers });
      toast.success(`Updated ${resp.data.updated} product(s)`);
      if (resp.data.errors?.length) {
        resp.data.errors.forEach(e => toast.error(`${e.product_id}: ${e.error}`));
      }
      setEditedRows({});
      fetchProducts();
    } catch (err) {
      toast.error("Bulk save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleMassApply = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error("No products selected"); return; }

    const payload = { product_ids: ids };
    let hasValue = false;
    for (const [field, val] of Object.entries(massApplyData)) {
      if (val !== "" && val !== null) {
        const num = parseFloat(val);
        if (isNaN(num) || num < 0) {
          toast.error(`Invalid ${field.replace("shipping_", "")} value`);
          return;
        }
        payload[field] = num;
        hasValue = true;
      }
    }

    if (!hasValue) {
      toast.error("Enter at least one value to apply");
      return;
    }

    setSaving(true);
    try {
      const resp = await axios.put(`${API}/admin/shipping-specs/mass-apply`, payload, { headers });
      toast.success(`Applied to ${resp.data.updated} product(s)`);
      setSelectedIds(new Set());
      setShowMassApply(false);
      setMassApplyData({ weight: "", shipping_length: "", shipping_width: "", shipping_height: "" });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mass apply failed");
    } finally {
      setSaving(false);
    }
  };

  const exportMissing = async () => {
    try {
      const resp = await axios.get(`${API}/admin/shipping-specs/export-missing`, {
        headers,
        params: { filter: activeFilter || "missing_any" },
      });
      const data = resp.data.products || [];
      if (data.length === 0) {
        toast.info("No products to export");
        return;
      }
      const csvHeaders = ["name", "sku", "brand", "category", "price", "stock", "weight", "length", "width", "height"];
      const csvRows = [csvHeaders.join(",")];
      data.forEach(p => {
        csvRows.push(csvHeaders.map(h => {
          const v = p[h];
          if (typeof v === "string" && (v.includes(",") || v.includes('"'))) {
            return `"${v.replace(/"/g, '""')}"`;
          }
          return v ?? "";
        }).join(","));
      });
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `missing_shipping_specs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} products`);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.product_id)));
    }
  };

  const editedCount = Object.keys(editedRows).length;

  const filterTabs = [
    { key: null, label: "All", count: summary.total_products },
    { key: "missing_any", label: "Missing Any", count: summary.missing_any },
    { key: "missing_weight", label: "Missing Weight", count: summary.missing_weight },
    { key: "missing_dimensions", label: "Missing Dims", count: summary.missing_dimensions },
    { key: "complete", label: "Complete", count: summary.complete },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="bulk-shipping-editor">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/admin/dashboard")} className="text-gray-400 hover:text-gray-700 transition-colors" data-testid="back-to-admin-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" data-testid="page-title">
                  Bulk Shipping Editor
                </h1>
                <p className="text-sm text-gray-500 hidden sm:block">Quickly manage product shipping specifications</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportMissing}
                className="hidden sm:flex items-center gap-1.5"
                data-testid="export-missing-btn"
              >
                <Download className="w-4 h-4" /> Export Missing
              </Button>
              {selectedIds.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMassApply(true)}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="mass-apply-btn"
                >
                  <Layers className="w-4 h-4 mr-1" /> Mass Apply ({selectedIds.size})
                </Button>
              )}
              {editedCount > 0 && (
                <Button
                  size="sm"
                  onClick={saveAllEdited}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90 text-white"
                  data-testid="save-all-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save All ({editedCount})
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="filter-tabs">
          {filterTabs.map(tab => (
            <button
              key={tab.key ?? "all"}
              onClick={() => { setActiveFilter(tab.key); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                activeFilter === tab.key
                  ? "bg-gray-900 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
              data-testid={`filter-${tab.key ?? "all"}`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`ml-1.5 text-xs ${activeFilter === tab.key ? "text-gray-300" : "text-gray-400"}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search & Additional Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, SKU, or brand..."
              className="pl-9 h-9 text-sm"
              data-testid="search-input"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-sm border border-gray-200 rounded-md bg-white"
            data-testid="category-filter"
          >
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.category_id || c.slug} value={c.slug}>{c.name}</option>
            ))}
          </select>
          <Input
            value={brandFilter}
            onChange={(e) => { setBrandFilter(e.target.value); setPage(1); }}
            placeholder="Brand..."
            className="h-9 text-sm w-32"
            data-testid="brand-filter"
          />
          <span className="text-sm text-gray-500" data-testid="result-count">
            {total} product{total !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Mass Apply Panel */}
        {showMassApply && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="mass-apply-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-blue-900">
                Mass Apply to {selectedIds.size} selected product{selectedIds.size !== 1 ? "s" : ""}
              </h3>
              <button onClick={() => setShowMassApply(false)} className="text-blue-400 hover:text-blue-600">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: "weight", label: "Weight (lbs)" },
                { key: "shipping_length", label: "Length (in)" },
                { key: "shipping_width", label: "Width (in)" },
                { key: "shipping_height", label: "Height (in)" },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs font-medium text-blue-700 block mb-1">{f.label}</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={massApplyData[f.key]}
                    onChange={(e) => setMassApplyData(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="h-8 text-sm"
                    data-testid={`mass-apply-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={handleMassApply} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="mass-apply-confirm-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Apply
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowMassApply(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="shipping-specs-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={products.length > 0 && selectedIds.size === products.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-700 min-w-[200px]">Product</th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-700 w-28">SKU</th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-700 w-24">Brand</th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-700 w-28">Category</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-700 w-24">Weight (lbs)</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-700 w-24">L (in)</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-700 w-24">W (in)</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-700 w-24">H (in)</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-700 w-32">Status</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-700 w-20">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                      <p className="text-gray-500 mt-2 text-sm">Loading products...</p>
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12">
                      <Package className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-gray-500 text-sm">No products found matching your filters.</p>
                    </td>
                  </tr>
                ) : (
                  products.map((p) => {
                    const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.missing_all;
                    const StatusIcon = cfg.icon;
                    const edited = isRowEdited(p.product_id);
                    return (
                      <tr
                        key={p.product_id}
                        className={`transition-colors ${edited ? "bg-amber-50/50" : "hover:bg-gray-50"} ${selectedIds.has(p.product_id) ? "bg-blue-50/30" : ""}`}
                        data-testid={`product-row-${p.product_id}`}
                      >
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.product_id)}
                            onChange={() => toggleSelect(p.product_id)}
                            className="w-4 h-4 rounded border-gray-300"
                            data-testid={`select-${p.product_id}`}
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="font-medium text-gray-900 line-clamp-1" title={p.name}>{p.name}</span>
                        </td>
                        <td className="px-3 py-2.5 text-gray-600 font-mono text-xs">{p.sku || "—"}</td>
                        <td className="px-3 py-2.5 text-gray-600 text-xs">{p.brand || "—"}</td>
                        <td className="px-3 py-2.5 text-gray-600 text-xs capitalize">{p.category_name || p.category || "—"}</td>
                        {["weight", "shipping_length", "shipping_width", "shipping_height"].map(field => (
                          <td key={field} className="px-2 py-1.5">
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={getFieldValue(p, field)}
                              onChange={(e) => handleFieldChange(p.product_id, field, e.target.value)}
                              className={`w-full h-8 px-2 text-center text-sm border rounded-md transition-colors focus:outline-none focus:ring-1 focus:ring-primary ${
                                getFieldValue(p, field) === "" ? "border-red-200 bg-red-50/50" : "border-gray-200 bg-white"
                              } ${edited ? "border-amber-300" : ""}`}
                              placeholder="—"
                              data-testid={`${field}-${p.product_id}`}
                            />
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`} data-testid={`status-${p.product_id}`}>
                            <StatusIcon className="w-3 h-3" />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {edited && (
                            <button
                              onClick={() => saveRow(p.product_id)}
                              disabled={saving}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                              data-testid={`save-row-${p.product_id}`}
                            >
                              <Save className="w-3 h-3" /> Save
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages} ({total} products)
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkShippingEditorPage;
